/*
    ********************************************************************************
      This piece of code has been taken from the open source npm package "oidc-react".
      The code has been modified on the part where it stores the the session user data
      in the localStorage instead of the sessionStorage.
    ********************************************************************************
*/

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useContext,
  PropsWithChildren,
} from 'react';
import {
  SigninRedirectArgs,
  SignoutRedirectArgs,
  User,
  UserManager,
  WebStorageStateStore,
} from 'oidc-client-ts';
import {
  AuthContextProps,
  AuthProviderProps,
  Location,
} from '../../../@types/AuthContext.type';
import { getBaseURL } from '../../../utils/authHandlers';
import appSettings from '../../../appSettings';

export const AuthContext = React.createContext<AuthContextProps | undefined>(
  undefined,
);

export const hasCodeInUrl = (location: Location) => {
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace('#', '?'));

  return Boolean(
    searchParams.get('code') ||
      searchParams.get('id_token') ||
      searchParams.get('session_state') ||
      hashParams.get('code') ||
      hashParams.get('id_token') ||
      hashParams.get('session_state'),
  );
};

const baseUrl = getBaseURL();

export const oidcConfig = {
  authority: appSettings.OIDC_PROVIDER,
  client_id: appSettings.OIDC_CLIENT_ID,
  response_type: 'code',
  redirect_uri: `${baseUrl}${appSettings.OIDC_REDIRECT_ROUTE}`,
  silent_redirect_uri: `${baseUrl}${appSettings.OIDC_REDIRECT_ROUTE}`,
  post_logout_redirect_uri: `${baseUrl}${appSettings.OIDC_REDIRECT_ROUTE}`,
  loadUserInfo: true,
  automaticSilentRenew: true,
  scope: 'openid profile email',
  userStore: new WebStorageStateStore({ store: globalThis.localStorage }),

  metadata: {
    issuer: appSettings.OIDC_PROVIDER,
    authorization_endpoint: `${appSettings.OIDC_PROVIDER}/as/authorization.oauth2`,
    token_endpoint: `${appSettings.OIDC_PROVIDER}/as/token.oauth2`,
    userinfo_endpoint: `${appSettings.OIDC_PROVIDER}/idp/userinfo.openid`,
    jwks_uri: `${appSettings.OIDC_PROVIDER}/pf/JWKS`,
    end_session_endpoint: `${appSettings.OIDC_END_SESSION_ENDPOINT}`,
  },
};

export const initUserManager = (props: AuthProviderProps) => {
  if (props.userManager) {
    return props.userManager;
  }
  return new UserManager(oidcConfig);
};

export const AuthProvider: React.FC<PropsWithChildren<AuthProviderProps>> = ({
  children,
  autoSignIn = true,
  autoSignInArgs,
  autoSignOut = true,
  autoSignOutArgs,
  onBeforeSignIn,
  onSignIn,
  onSignOut,
  location = globalThis.location,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line no-self-compare
  const [userData, setUserData] = useState<null | User>(null);
  const [userManager] = useState(() => initUserManager(props));
  const isMountedRef = useRef(false);

  const isAuthenticated = !!userData && !userData.expired;

  const signOutHooks = useCallback(async () => {
    setUserData(null);
    onSignOut?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSignOut]);

  const signInPopupHooks = useCallback(async () => {
    const userFromPopup = await userManager.signinPopup();

    setUserData(userFromPopup);

    onSignIn?.(userFromPopup);
    await userManager.signinPopupCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userManager, onSignIn]);

  /**
   * Handles user auth flow on initial render.
   */
  useEffect(() => {
    let isMounted = true;
    isMountedRef.current = true;
    setIsLoading(true);
    (async () => {
      const user = await userManager?.getUser();
      // isMountedRef cannot be used here as its value is updated by next useEffect.
      // We intend to keep context of current useEffect.
      if (isMounted && (!user || user.expired)) {
        // If the user is returning back from the OIDC provider, get and set the user data.
        if (hasCodeInUrl(location)) {
          // eslint-disable-next-line @typescript-eslint/no-shadow
          const user = (await userManager.signinCallback()) ?? null;

          setUserData(user);
          onSignIn?.(user);
        }
        // If autoSignIn is enabled, redirect to the OIDC provider.
        else if (autoSignIn) {
          const state = onBeforeSignIn ? onBeforeSignIn() : undefined;
          await userManager.signinRedirect({ ...autoSignInArgs, state });
        }
      }
      // Otherwise if the user is already signed in, set the user data.
      else if (isMountedRef.current) {
        setUserData(user);
      }
      setIsLoading(false);
    })();
    return () => {
      isMounted = false;
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, userManager, autoSignIn, onBeforeSignIn, onSignIn]);

  /**
   * Registers UserManager event callbacks for handling changes to user state due to automaticSilentRenew, session expiry, etc.
   */
  useEffect(() => {
    const updateUserData = (user: User) => {
      isMountedRef.current && setUserData(user);
    };
    const onSilentRenewError = async () => {
      if (autoSignOut) {
        await signOutHooks();
        await userManager.signoutRedirect(autoSignOutArgs);
      }
    };
    userManager.events.addUserLoaded(updateUserData);
    userManager.events.addSilentRenewError(onSilentRenewError);
    return () => {
      userManager.events.removeUserLoaded(updateUserData);
      userManager.events.removeSilentRenewError(onSilentRenewError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userManager]);

  const value = useMemo(() => {
    return {
      signIn: async (args?: SigninRedirectArgs) => {
        await userManager.signinRedirect(args);
      },
      signInPopup: async () => {
        await signInPopupHooks();
      },
      signOut: async () => {
        await userManager.removeUser();
        await signOutHooks();
      },
      signOutRedirect: async (args?: SignoutRedirectArgs): Promise<void> => {
        await userManager.signoutRedirect(args);
        await signOutHooks();
      },
      userManager,
      userData,
      isLoading,
      isAuthenticated,
    };
  }, [
    userManager,
    isLoading,
    userData,
    isAuthenticated,
    signInPopupHooks,
    signOutHooks,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'AuthProvider context is undefined, please verify you are calling useAuth() as child of a <AuthProvider> component.',
    );
  }

  return context;
};
